import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { db } from '../../config/firebase';
import { RequestPassengerModal } from '../../components/flexy-ride/RequestPassengerModal';
import {
  MapPin,
  Calendar,
  Clock,
  Users,
  MessageCircle,
  ArrowLeft,
} from 'lucide-react';

const getSmokingLabel = (value, t) => {
  if (value === 'yes') return t('flexyRidePassenger.yes');
  if (value === 'no') return t('flexyRidePassenger.no');
  return t('flexyRidePassenger.flexible');
};

const getPetsLabel = (value, t) => {
  if (value === 'yes') return t('flexyRidePassenger.yes');
  if (value === 'no') return t('flexyRidePassenger.no');
  return t('flexyRidePassenger.flexible');
};

const getLuggageLabel = (value, t) => {
  if (value === 'small') return t('flexyRideSeat.luggageSmall');
  if (value === 'medium') return t('flexyRideSeat.luggageMedium');
  if (value === 'large') return t('flexyRideSeat.luggageLarge');
  return value || '-';
};

const getComfortLabel = (value, t) => {
  if (value === 'quiet') return t('flexyRidePassenger.comfortQuiet');
  if (value === 'chat') return t('flexyRidePassenger.comfortChat');
  return t('flexyRidePassenger.flexible');
};

export default function FlexyRidePassengerDetail() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = usePublicAuth();
  const { t } = useTranslation();
  const [listing, setListing] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [appliedListingIds, setAppliedListingIds] = useState(new Set());

  const isOwnListing = user?.uid === listing?.createdBy;

  useEffect(() => {
    if (!listingId) {
      setError(t('flexyRideDetail.listingIdNotFound'));
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      doc(db, 'ride_listings', listingId),
      snapshot => {
        if (!snapshot.exists()) {
          setError(t('flexyRideDetail.listingNotFound'));
          setLoading(false);
          return;
        }
        setListing({ id: snapshot.id, ...snapshot.data() });
        setLoading(false);
      },
      err => {
        console.error('Error loading listing:', err);
        setError(t('flexyRideDetail.failedToLoadListing'));
        setLoading(false);
      },
    );

    return () => unsub();
  }, [listingId]);

  useEffect(() => {
    if (!listing?.createdBy) return;

    const unsub = onSnapshot(
      doc(db, 'users', listing.createdBy),
      snapshot => {
        if (snapshot.exists()) {
          setOwnerProfile({ id: snapshot.id, ...snapshot.data() });
        }
      },
      err => {
        console.error('Error loading owner profile:', err);
      },
    );

    return () => unsub();
  }, [listing?.createdBy]);

  useEffect(() => {
    if (!user?.uid || !listingId) {
      setAppliedListingIds(new Set());
      return;
    }
    const unsub = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('applicantId', '==', user.uid),
        where('status', 'in', [
          'pending',
          'accepted',
          'confirm_pending',
          'completed',
        ]),
      ),
      snapshot => {
        if (!snapshot?.docs) {
          setAppliedListingIds(new Set());
          return;
        }
        const ids = new Set(
          snapshot.docs.map(doc => doc.data()?.listingId).filter(Boolean),
        );
        setAppliedListingIds(ids);
      },
    );
    return () => unsub();
  }, [user?.uid, listingId]);

  const handleApply = async (listingData, { price, carBrand, note }) => {
    if (!user?.uid) {
      alert(t('flexyRideDetail.passenger.applyLoginDriver'));
      return;
    }

    if (user.uid === listingData.createdBy) {
      alert(t('flexyRideDetail.applyOwnListing'));
      return;
    }

    setSubmitLoading(true);
    try {
      const existingSnapshot = await getDocs(
        query(
          collection(db, 'ride_requests'),
          where('listingId', '==', listingData.id),
          where('applicantId', '==', user.uid),
        ),
      );

      const activeExists = existingSnapshot.docs.some(doc => {
        const status = doc.data()?.status;
        return status === 'pending' || status === 'accepted';
      });

      if (activeExists) {
        alert(t('flexyRideDetail.alreadyApplied'));
        setAppliedListingIds(prev => {
          const next = new Set(prev);
          next.add(listingData.id);
          return next;
        });
        setShowPassengerModal(false);
        setSubmitLoading(false);
        return;
      }

      const listingOwnerName =
        ownerProfile?.displayName ||
        ownerProfile?.username ||
        ownerProfile?.email?.split('@')[0] ||
        user.displayName ||
        t('common.unknownUser');

      await addDoc(collection(db, 'ride_requests'), {
        listingId: listingData.id,
        listingOwnerId: listingData.createdBy,
        listingOwnerName: listingOwnerName,
        applicantId: user.uid,
        applicantName:
          user.displayName ||
          user.email?.split('@')[0] ||
          t('common.unknownUser'),
        applicantEmail: user.email,
        requesterId: user.uid,
        requesterName:
          user.displayName ||
          user.email?.split('@')[0] ||
          t('common.unknownUser'),
        requesterEmail: user.email,
        tripOwnerId: listingData.createdBy,
        tripOwnerName: listingOwnerName,
        status: 'pending',
        listingType: 'passenger',
        listingMode: listingData.mode || 'ride',
        price: price || '',
        carBrand: carBrand || '',
        note: note || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert(t('flexyRideDetail.requestSent'));
      setAppliedListingIds(prev => {
        const next = new Set(prev);
        next.add(listingData.id);
        return next;
      });
    } catch (error) {
      console.error('Error applying to listing:', error);
      alert(t('flexyRideDetail.failedToSendRequest'));
    } finally {
      setSubmitLoading(false);
      setShowPassengerModal(false);
    }
  };

  const handleMessage = () => {
    if (!user) {
      alert(t('flexyRideDetail.passenger.messageLogin'));
      return;
    }
    if (ownerProfile?.email) {
      navigate(`/chat`, { state: { participantEmail: ownerProfile.email } });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg">
            <div className="h-6 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
            <div className="mt-6 space-y-3">
              <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
              <div className="h-4 w-11/12 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 text-center shadow-lg">
            <p className="text-gray-600 dark:text-slate-400">
              {error || t('flexyRideDetail.listingNotFound')}
            </p>
            <Link
              to="/flexy-ride/passenger"
              className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('flexyRideDetail.backToListings')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const ownerName =
    ownerProfile?.displayName ||
    ownerProfile?.username ||
    ownerProfile?.email?.split('@')[0] ||
    t('tripCard.flexyUser');
  const ownerRating = ownerProfile?.averageRating ?? ownerProfile?.rating ?? 0;
  const ownerReviewCount =
    ownerProfile?.totalReviews ?? ownerProfile?.reviewsCount ?? 0;
  const isVerified = ownerProfile?.verificationStatus === 'verified';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />
      <main className="mx-auto max-w-4xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/flexy-ride/passenger')}
          className="mb-6 flex items-center gap-2 text-sm font-semibold text-gray-600 transition hover:text-gray-900 dark:text-slate-400 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('flexyRideDetail.backToListings')}
        </button>

        {/* Owner Profile Card */}
        {ownerProfile && (
          <div className="mb-6 rounded-3xl bg-white p-6 text-center shadow-lg dark:bg-slate-900">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-black text-3xl font-bold text-yellow-400">
              {ownerName.charAt(0).toUpperCase()}
            </div>
            <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">
              {ownerName}
            </h2>
            {ownerProfile.username && (
              <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                @{ownerProfile.username}
              </p>
            )}
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-lg">
                {'⭐'.repeat(Math.max(1, Math.round(ownerRating || 0)))}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {ownerRating ? ownerRating.toFixed(1) : '—'}
              </span>
              {ownerReviewCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-slate-400">
                  ({ownerReviewCount} reviews)
                </span>
              )}
            </div>
            <div className="mt-3">
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                  isVerified
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-amber-500/10 text-amber-300'
                }`}
              >
                {isVerified
                  ? t('flexyRideDetail.verifiedPassenger')
                  : t('flexyRideDetail.unverifiedPassenger')}
              </span>
            </div>
          </div>
        )}

        {/* Listing Details Card */}
        <div className="rounded-3xl bg-white p-6 shadow-lg dark:bg-slate-900">
          <div className="mb-6">
            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-800 dark:bg-blue-500/20 dark:text-blue-400">
              {t('flexyRideDetail.passengerBadge')}
            </span>
          </div>

          {/* Route */}
          <section className="mb-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              {t('flexyRideDetail.route')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('home.from')}
                  </p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {listing.from || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('home.to')}
                  </p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {listing.to || '-'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="my-6 border-t border-gray-200 dark:border-slate-700" />

          {/* Departure */}
          <section className="mb-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              {t('travelerTripRequests.departure')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('requestDelivery.date')}
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {listing.date || t('flexyRideSeat.dateNotSet')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('requestDelivery.time')}
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {listing.time || t('flexyRideSeat.timeNotSet')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="mt-0.5 h-5 w-5 text-gray-500 dark:text-slate-400" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {t('flexyRidePassenger.seats')}
                  </p>
                  <p className="text-base text-gray-900 dark:text-white">
                    {t('flexyRidePassenger.summarySeatsValue', {
                      count: listing.seats || 1,
                    })}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="my-6 border-t border-gray-200 dark:border-slate-700" />

          {/* Preferences */}
          <section className="mb-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
              {t('flexyRidePassenger.preferencesTitle')}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRidePassenger.smoking')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getSmokingLabel(listing.smoking)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRidePassenger.pets')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getPetsLabel(listing.pets)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRidePassenger.luggage')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getLuggageLabel(listing.luggage)}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRidePassenger.comfort')}
                </p>
                <p className="text-base text-gray-900 dark:text-white">
                  {getComfortLabel(listing.comfort)}
                </p>
              </div>
            </div>
          </section>

          {/* Preferred Car */}
          {listing.carBrand && (
            <>
              <div className="my-6 border-t border-gray-200 dark:border-slate-700" />
              <section className="mb-6">
                <h3 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">
                  {t('flexyRideDetail.preferredCar')}
                </h3>
                <p className="text-base text-gray-900 dark:text-white">
                  {listing.carBrand}
                  {listing.carModel ? ` ${listing.carModel}` : ''}
                </p>
              </section>
            </>
          )}

          {/* Note */}
          {listing.note && (
            <>
              <div className="my-6 border-t border-gray-200 dark:border-slate-700" />
              <section className="mb-6">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('flexyRidePassenger.summaryNote')}
                </p>
                <p className="text-base italic text-gray-700 dark:text-slate-300">
                  {listing.note}
                </p>
              </section>
            </>
          )}

          {/* Action Buttons */}
          {!isOwnListing && (
            <>
              <div className="my-6 border-t border-gray-200 dark:border-slate-700" />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (!user) {
                      alert(t('flexyRideDetail.passenger.applyLoginDriver'));
                      return;
                    }
                    if (appliedListingIds.has(listing.id)) {
                      alert(t('flexyRideDetail.alreadyApplied'));
                      return;
                    }
                    setShowPassengerModal(true);
                  }}
                  disabled={!user || appliedListingIds.has(listing.id)}
                  className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                    !user || appliedListingIds.has(listing.id)
                      ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-slate-800 dark:text-slate-500'
                      : 'bg-yellow-400 text-black hover:bg-yellow-300'
                  }`}
                >
                  {!user
                    ? t('flexyRideDetail.signInToApplyDriver')
                    : appliedListingIds.has(listing.id)
                    ? t('flexyRideDetail.applied')
                    : t('flexyRideDetail.applyAsDriver')}
                </button>
                <button
                  onClick={handleMessage}
                  disabled={!user}
                  className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
                    !user
                      ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-slate-800 dark:text-slate-500'
                      : 'flex items-center justify-center gap-2 bg-black text-yellow-400 hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400'
                  }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  {t('myRequests.message')}
                </button>
              </div>
            </>
          )}
        </div>
      </main>

      <RequestPassengerModal
        visible={showPassengerModal}
        onClose={() => setShowPassengerModal(false)}
        onSubmit={data => listing && handleApply(listing, data)}
        loading={submitLoading}
      />
    </div>
  );
}
